import {LoadingDots} from '@commercial-helios/ui/src/components/loading-dots'
import React from 'react'
import styled from 'styled-components'

import type {State} from '@/components/hubspot-form'
import {HubspotForm} from '@/components/hubspot-form'
import InlineWistiaVideo from '@/components/inline-wistia-video'
import {tokens} from '@helios/../packages/ui/theme'
import {useLocalStorage} from '@helios/shared/hooks/use-storage'
import {BackgroundWrapper, Layout, Section} from '@helios/shared/styles/grid'
import {Text} from '@helios/shared/styles/typography'
import type {FormConfig, ImperativeConsent} from '@helios/shared/types/consents'
import type ImageSource from '@helios/shared/types/image'
import mq from '@helios/shared/utils/media-query'
import replacePlaceholders from '@helios/shared/utils/replace-placeholders'

export const OTHER = 'Other' as const

type Props = {
    wistiaVideoId: string
    thumbnail: ImageSource
    formTitle: string
    formDescription: string
    submitButtonText: string
    successMessage: string
    recurrentVisitorMessage: string
    formConfig: FormConfig
    imperativeConsents?: ImperativeConsent[]
    onRequest: () => void
}

enum ResourceFormState {
    LOADING = 'loading',
    FORM = 'form',
    SUBMITTED = 'submitted',
    RECURRING_VISIT = 'recurring_visit',
}

export const OnDemandResource = ({
    formConfig,
    imperativeConsents,
    onRequest,
    ...content
}: Props) => {
    const [formDetails, setFormDetails] = useLocalStorage<State>('formDetails', {} as State)

    const [formState, setFormState] = React.useState<ResourceFormState>(ResourceFormState.LOADING)

    const hasSubmittedForm = Object.keys(formDetails).length > 0

    React.useEffect(() => {
        if (formState === ResourceFormState.LOADING && hasSubmittedForm) {
            setFormState(ResourceFormState.RECURRING_VISIT)
            return
        }

        if (formState === ResourceFormState.LOADING && !hasSubmittedForm) {
            setFormState(ResourceFormState.FORM)
            return
        }
    }, [formState, hasSubmittedForm])

    const successMessage = replacePlaceholders(content.successMessage, {
        '{{name}}': formDetails.firstName,
    })
    const recurrentVisitorMessage = replacePlaceholders(content.recurrentVisitorMessage, {
        '{{name}}': formDetails.firstName,
    })
    const message =
        formState === ResourceFormState.RECURRING_VISIT ? recurrentVisitorMessage : successMessage

    return (
        <BackgroundWrapper color="grey">
            {formState === ResourceFormState.LOADING && (
                <Section>
                    <LoadingDotsWrapper>
                        <LoadingDots />
                    </LoadingDotsWrapper>
                </Section>
            )}

            {formState === ResourceFormState.FORM && (
                <StyledSection>
                    <Layout variant="two-asymmetrical">
                        <FormTitleWrapper>
                            <FormTitle forwardedAs="h2" variant="heading-base">
                                {content.formTitle}
                            </FormTitle>
                            <Text variant="paragraph-base">{content.formDescription}</Text>
                        </FormTitleWrapper>
                        <HubspotForm
                            formConfig={formConfig}
                            redirectCallback={(state) => {
                                setFormState(ResourceFormState.SUBMITTED)
                                setFormDetails(state)
                                onRequest()
                            }}
                            hiddenFields={['discoveredVia', 'phoneNumber']}
                            imperativeConsents={imperativeConsents}
                            content={{
                                submitButtonText: content.submitButtonText,
                            }}
                        />
                    </Layout>
                </StyledSection>
            )}

            {(formState === ResourceFormState.SUBMITTED ||
                formState === ResourceFormState.RECURRING_VISIT) && (
                <Section>
                    <Layout variant="two-asymmetrical">
                        <SuccessMessage forwardedAs="h2" variant="heading-base">
                            {message}
                        </SuccessMessage>
                    </Layout>
                    <InlineWistiaVideo
                        thumbnail={content.thumbnail}
                        videoId={content.wistiaVideoId}
                    />
                </Section>
            )}
        </BackgroundWrapper>
    )
}

const StyledSection = styled(Section)`
    padding-left: 0;
    padding-right: 0;

    ${mq.min('sm')} {
        padding-left: ${tokens.spacing24};
        padding-right: ${tokens.spacing24};
    }
`

const FormTitleWrapper = styled.div`
    padding-left: ${tokens.spacing24};
    padding-right: ${tokens.spacing24};

    ${mq.min('sm')} {
        padding-left: 0;
        padding-right: 0;
    }
`

const FormTitle = styled(Text)`
    margin-bottom: ${tokens.spacing20};
`

const SuccessMessage = styled(Text)`
    margin-bottom: ${tokens.spacing24};

    ${mq.min('sm')} {
        margin-bottom: 100px;
    }
`

const LoadingDotsWrapper = styled.div`
    position: relative;
    aspect-ratio: 4/3;
`
